import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import { formatBid, bumpRatePercentToDecimal } from './bump_helpers';
import {
  MAX_BUMP_AMOUNT_PERCENT,
  MIN_BUMP_AMOUNT_PERCENT,
  BUMP_INCREMENT_AMOUNT_PERCENT,
} from '../shared/constants';
import { RCAlertBox, RCIconButton, RCTextInput } from '@reverbdotcom/cadence/components';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { PencilSimpleIcon } from '@reverbdotcom/cadence/icons/react';

function OptOutLabel() {
  return (
    <span className="text-default-400 text-default--bold">{I18n.t('discovery.bumpModal.optOutTwoRadio')}</span>
  );
}

export function RecommendedRadioV2({
  recommendationAmount,
  selectedBid,
  handleBidChange,
  showCustomWarning,
  setShowCustomWarning,
}: {
  recommendationAmount: number;
  selectedBid: number;
  handleBidChange: (bid: number) => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (boolean) => void;
}) {
  const [usingCustom, setUsingCustom] = React.useState(
    (selectedBid !== 0 && ((bumpRatePercentToDecimal(recommendationAmount)) !== selectedBid)),
  );

  const [usingPromote, setUsingPromote] = React.useState(true);
  const [usingNoBump, setUsingNoBump] = React.useState(false);

  const [customBid, setCustomBid] = React.useState(
    (selectedBid !== 0) ? (formatBid(selectedBid)) : recommendationAmount,
  );

  const [isEditing, setIsEditing] = React.useState(false);

  React.useEffect(() => {
    if (usingPromote) {
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    }
  }, [usingPromote]);

  const handlePromoteClick = (): void => {
    setUsingPromote(true);
    setUsingCustom(false);
    setUsingNoBump(false);
    setShowCustomWarning(false);

    handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
  };

  const handleEditClick = (): void => {
    setUsingCustom(true);
    setUsingNoBump(false);
    setIsEditing(true);

    setCustomWarning(customBid);
    handleBidChange(bumpRatePercentToDecimal(customBid));
  };

  const handleNoBumpClick = (): void => {
    setUsingPromote(false);
    setUsingCustom(false);
    setUsingNoBump(true);
    setShowCustomWarning(false);
    setIsEditing(false);
    setCustomBid(!showCustomWarning ? customBid : recommendationAmount);

    handleBidChange(0);
  };

  const handleCustomSelectChange = (event: any) => {
    const bid = event?.target?.value;
    setCustomWarning(bid);
    setCustomBid(bid);
    handleBidChange(bumpRatePercentToDecimal(bid));
  };

  const setCustomWarning = (bid: number) => {
    if ((bid >= MIN_BUMP_AMOUNT_PERCENT) && (bid <= MAX_BUMP_AMOUNT_PERCENT) && (bid % BUMP_INCREMENT_AMOUNT_PERCENT === 0)) {
      setShowCustomWarning(false);
    } else {
      setShowCustomWarning(true);
    }
  };

  function RecommendedMode() {
    return (
      <div className="recommended-radio__recommended-mode-container">
        <div className="recommended-radio__input-label">
          <b><Translate text="discovery.bumpModal.bumpRateSetting" /></b>
        </div>
        <div>
          <span className="pr-3 text-title-600">{customBid ?? recommendationAmount}%</span>
          <RCIconButton
            label="Edit Rate"
            size="mini"
            variant="muted"
            svgComponent={PencilSimpleIcon}
            onClick={handleEditClick}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classNames(
        'bd-2 bd-radius-md',
        { 'bd-color-selected': usingPromote },
        { 'bd-color-selectable': !(usingPromote) },
      )}>
        <div className="d-flex fx-justify-between padding-4">
          <FormGroupWithRadio
            label={<b><Translate className="text-default-400 text-default--bold" text="discovery.bumpModal.promoteMyListing"></Translate></b>}
            inputName="bump-modal-radios"
            id="bump-modal-radio-recommended"
            updateField={handlePromoteClick}
            value={`${recommendationAmount / 100}`}
            checked={usingPromote}
          />
        </div>
        {usingPromote &&
          <div className="recommended-radio__footer">
            {isEditing ? (
              <>
                <div className="recommended-radio__editing-mode-container">
                  <div className="recommended-radio__input-label">
                    <b><Translate text="discovery.bumpModal.bumpRateSetting" /></b>
                    {
                      showCustomWarning && <div className="recommended-radio__warning">
                        <RCAlertBox type="error" small>
                          {I18n.t('discovery.bumpModal.customBumpWarning', {
                            minBump: MIN_BUMP_AMOUNT_PERCENT,
                            maxBump: MAX_BUMP_AMOUNT_PERCENT,
                            bumpIncrement: BUMP_INCREMENT_AMOUNT_PERCENT,
                          })}
                        </RCAlertBox>
                      </div>
                    }
                  </div>
                  <div className="recommended-radio__input">
                    <RCTextInput
                      type="number"
                      label=""
                      disabled={!(usingCustom)}
                      value={customBid.toString()}
                      onChange={handleCustomSelectChange}
                      suffixText="%"
                    />
                  </div>
                </div>
                <div className="recommended-radio__edit-blue-container">
                  <Translate
                    className="text-default-350 text-default--bold"
                    text="discovery.bumpModal.recommendedRateTitle"
                    args={{ percent: recommendationAmount.toString() }}
                  />
                  <Translate
                    className="text-fine-300"
                    text="discovery.bumpModal.recommendedRateBody"
                  />
                </div>
              </>
            ) : (
              <RecommendedMode />
            )}
            <div className="plr-6 pb-6 text-default-350">
              <Translate text="discovery.bumpModal.bumpRateSettingDescription" />
            </div>
          </div>
        }
      </div >
      <div className={classNames(
        'bd-2 bd-radius-md padding-4 mt-2',
        { 'bd-color-selected': usingNoBump },
        { 'bd-color-selectable': !(usingNoBump) },
      )}>
        <FormGroupWithRadio
          label={<OptOutLabel />}
          inputName="bump-modal-radios"
          id="bump-modal-radio-no-bump"
          updateField={handleNoBumpClick}
          value={`${0}`}
          checked={usingNoBump}
        />
      </div>
    </>
  );
}
